import { useEffect, useState } from "react";
import { fetchAndActivate, getString } from "firebase/remote-config";
import { remoteConfig } from "@/firebase/firebase";

/**
 * A hook to fetch a string value from Firebase Remote Config.
 *
 * @param key The parameter key in Remote Config.
 * @param defaultValue The value to use while loading or if the fetch fails/returns empty.
 * @returns The configuration value.
 */
export function useRemoteConfigString(
  key: string,
  defaultValue: string
): string {
  const [value, setValue] = useState<string>(defaultValue);

  useEffect(() => {
    let isMounted = true;

    const fetchConfig = async () => {
      try {
        // This leverages the SDK's internal caching.
        // We assume 'remoteConfig' is already initialized in firebase.ts
        await fetchAndActivate(remoteConfig);

        if (isMounted) {
          const fetchedValue = getString(remoteConfig, key);
          // We define "valid remote value" as a non-empty string here.
          // If you intentionally want empty strings from remote, remove the truthiness check.
          if (fetchedValue) {
            setValue(fetchedValue);
          }
        }
      } catch (err) {
        console.error(`Error fetching remote config for key "${key}":`, err);
        // On error, we keep the defaultValue which is already set.
      }
    };

    fetchConfig();

    return () => {
      isMounted = false;
    };
  }, [key]);

  return value;
}
